import {Injectable} from "@angular/core";
import {BleCore} from "./BleCore";
import {hrvSimpleStats, hrvFftStats, hrvSampleData, hrvData, HrvStatus, HrvConfig} from "../model/hrv";
import {BLE} from "@awesome-cordova-plugins/ble/ngx";
import {Complex} from "../helpers/complex";
import {FFT} from "../helpers/fft";
import {Events} from "ionic-angular";
import {ClaEvents} from "../model/events";
import {Int64LE} from "int64-buffer";
import {Decimal} from "decimal.js";
import {LogService} from "../providers/logging/log.service";

@Injectable()
export class BleHrv extends BleCore {
  deviceId: string = '';

  fftBound = 1024;
  doubleFilter = 1.75;
  extraFilter = 0.4;
  extra2Filter = 0.85;
  finalFilter = 0.6;
  invalidScan: boolean = false;

  scrRunningAverage: number = 0;
  stepCounter: number = 0;
  TempReadings: hrvData[] = [];
  ScrReadings: hrvData[] = [];
  HbisReadings: hrvData[] = [];
  saveData: boolean = false;
  ScanSample: HrvSample = new HrvSample();
  Config: HrvConfig;
  Status: HrvStatus;
  connecting: boolean = false;

  constructor(private ble: BLE, public events: Events, public logger: LogService) {
    super(ble, events, logger);

  }

  public async Connect(id: string) : Promise<void> {
    this.events.subscribe(ClaEvents.DEVICE_CONNECTED, state => {
      this.logger.log(`DEVICE_CONNECTED: ${state}`);
      if(!this.connecting) return;

      this.connected = state;
      this.connecting = false;
      this.events.publish(ClaEvents.HRV_CONNECTED, true);
      this.events.unsubscribe(ClaEvents.DEVICE_CONNECTED);
    });
    await this.SetupDevice(id);
    this.connecting = true;
  }

  public async Disconnect() {
    //await this.StreamEnable(HrvStreams.Pgp, false);
    await super.Disconnect();
    this.events.publish(ClaEvents.HRV_DISCONNECTED, true);
  }

  public async StartScanning(): Promise<boolean> {
    this.ResetData();
    await this.StartStream();
    this.events.publish(ClaEvents.HRV_LISTENING, true);
    return true;
  }

  private ResetData() {
    this.HbisReadings = [];
    this.ScrReadings = [];
    this.TempReadings = [];
    this.ScanSample = new HrvSample();
    this.saveData = false;
    this.invalidScan = false;
  }

  private async StartStream(): Promise<boolean> {
    await this.StreamEnable(HrvStreams.Pgp, true);
    return true;
  }


  // encodes hrv data as an ASCII Byte String
  // The format of this is [8 bytes: Timestamp as Int64LE][4 bytes: Value as Float32LE][...]
  private encodeHrvObject(data: hrvData[]): string {
    // calculate length of data view
    // 12 bytes per data entry
    const dataViewBufferSize = data.length * 12;

    // create array buffer
    const buffer = new ArrayBuffer(dataViewBufferSize);

    // create data view to manipulate array buffer
    const view = new DataView(buffer);

    // loop all data entries
    data.forEach(({TimeStamp, DataValue}, index) => {

      // for some reason we cannot use BigInt here, so we will work with Int64LE

      // convert timestamp to Int64LE
      const bigTimeStamp = new Int64LE(TimeStamp);

      // convert to array buffer
      const bigTimeStampBuf = new Int8Array(bigTimeStamp.toArrayBuffer());

      // calculate main offset for this entry
      const offset = index * 12;

      // copy to main buffer at pos index * 12 (to account for previous entries) + i (for each Int8 entry)
      bigTimeStampBuf.forEach((v, i) =>
        view.setInt8(offset + i, v)
      );

      // now just write in the float value at pos index * 12 + 8 (to account for timestamp)
      view.setFloat32(offset + 8, DataValue, true);
    });

    // now we just convert the dataview into a string
    return Array.from(new Uint8Array(view.buffer)) // we can't use spread operator due to target js ver, so this is ugly
      .map(x => this.padStart(x.toString(16), 2, '0'))  // we have to shim pad string here
      .join('');
  }

  public async CompleteScan(time?: number) {
    await this.Disconnect();

    this.logger.debug(`Temp Readings ${JSON.stringify(this.TempReadings)}`,[{'SourceContext': 'BleHrv'}]);
    this.logger.debug(`SCR Readings ${JSON.stringify(this.ScrReadings)}`,[{'SourceContext': 'BleHrv'}]);
    this.logger.debug(`Hbis Readings ${JSON.stringify(this.HbisReadings)}`,[{'SourceContext': 'BleHrv'}]);
    let ibiResults = this.FilterData(this.HbisReadings.map(v => v));
    // check if we have a min of 30 beats per minute
    if(ibiResults == undefined) return;
    let simpleStats = BleHrv.CalcSimpleStats(ibiResults.length, ibiResults, this.logger);
    let fft: hrvFftStats = BleHrv.TransformFft(ibiResults.length, ibiResults, simpleStats.mean, 1024);

    this.logger.debug(`SimpleStats ${JSON.stringify(simpleStats)}`, simpleStats, [{'SourceContext': 'BleHrv'}]);

    const packet = {
      rawPpgs: this.encodeHrvObject(this.HbisReadings),
      rawScrs: this.encodeHrvObject(this.ScrReadings),
      rawTemps: this.encodeHrvObject(this.TempReadings),
      variance: simpleStats.variance,
      mean: simpleStats.mean,
      avgHr: simpleStats.averageHeartRate,
      rmsStdDev : simpleStats.rmsSuccessiveDifference,
      stdDev: simpleStats.standardDeviation,
      aa: fft.aA,
      ab: fft.aB,
      abIndicator: fft.aBIndicator,
      fft: fft.fftFinal,
      fftImaginary: fft.fftFinal,
      fftReal: fft.fftFinal,
      hf: fft.hf,
      n: ibiResults.length,
      invalidScan: this.invalidScan,
      lf: fft.lf,
      range: fft.range,
      rawBalance: fft.rawBalance,
      totalPower: fft.totalPower,
      vlf: fft.vlf
    }
    return packet;
  }

  public async ProcessStatus(data: ArrayBuffer) {
    const view = new DataView(data);
    this.Status = {
      BatteryVoltage: view.getUint16(12, true),
      DcInputVoltage: view.getUint16(10, true),
      FirmwareMajor: view.getUint8(7),
      FirmwareMinor: view.getUint8(6),
      Firmware: '',
      StatusFlags: view.getUint8(9),
      StreamEnabled: view.getUint8(8),
    }
    this.Status.Firmware = `${this.Status.FirmwareMajor}.${this.Status.FirmwareMinor}`;
    this.ReportPower(this.Status.BatteryVoltage, 4000, 3000);
    this.logger.debug(`ProcessStatus ${JSON.stringify(this.Status)}`, [{'SourceContext': 'BleHrv'}]);

    // only used for testing
    this.events.publish(ClaEvents.HRV_STATUS_RX, this.Status);
  }

  public async ProcessConfig(data: ArrayBuffer) {
    const view = new DataView(data);
    this.Config = {
      AlgoGain: view.getFloat32(25, true),
      AlgoOffset: view.getFloat32(29, true),
      ModuleAddress: 2,
      SclGain: view.getFloat32(17, true),
      SclOffset: view.getFloat32(21, true),
      SerialNumber: view.getUint32(7, true),
      TempGain: view.getFloat32(9, true),
      TempOffset: view.getFloat32(13, true),

    }
    this.logger.debug(`ProcessConfig ${JSON.stringify(this.Config)}`, [{'SourceContext': 'BleHrv'}]);

    // only used for testing
    this.events.publish(ClaEvents.HRV_CONFIG_RX, this.Config);
  }

  public async ProcessData(data: ArrayBuffer) {
    const view = new DataView(data);
    if(data.byteLength != 214) {
      this.logger.error(`HRV Invalid length packet ${JSON.stringify(this.Config)}`, [{'SourceContext': 'BleHrv'}]);
      this.logger.error(`TEMPDATABUFFER: ${this.TEMPDATABUFFER}`, [{'SourceContext': 'BleHrv'}]);
      this.logger.error(`data: ${JSON.stringify(data)}`, [{'SourceContext': 'BleHrv'}]);
      return;
    }
    const date = Date.now() * 10000;
    let streamType = view.getUint8(1);
    let msgId = view.getUint16(3, true);
    let hbi: hrvData;
    if(streamType === HrvStreams.Pgp) {
      let temp = view.getFloat32(5, true);
      this.TempReadings.push({ TimeStamp: date, DataValue: temp});
      let scl = view.getFloat32(9, true);
      let scr = this.CalculateScr(scl);
      this.ScrReadings.push({ TimeStamp: date, DataValue: scr });
      let rawSamples = data.slice(13, 212);
      const rawView = new DataView(rawSamples);
      for( let i = 0; i < rawSamples.byteLength - 1; i += 2) {
        let count = rawView.getUint16(i,true);
        this.ScanSample.samples.push(count);
        let sampleData: hrvSampleData = this.ScanSample.AddSample(count);
        if(sampleData != undefined && sampleData.interval > 0) {
          hbi = { TimeStamp: sampleData.timeStamp * 10000, DataValue: sampleData.interval }
          this.saveData && this.HbisReadings.push(hbi);
        }
      }
      const sendData = {
        hbis: hbi ? [hbi] : [],
        sampleCounts: this.ScanSample.samples,
        scr: scr.toFixed(2),
        temperature: new Decimal(temp),
        timestamp: date,
      }
      this.events.publish(ClaEvents.SCANDATA_HRV_RX, sendData);
      this.ScanSample.samples = [];

      // only used for testing
      this.events.publish(ClaEvents.HRV_DATA_RX, sendData);
    }
  }
  CalculateScr(scl: number) {
    const avgSample = 60;
    const maxScr = 8.125;

    // maintain DC offset by maintaining a running average
    this.scrRunningAverage *= (avgSample - 1);
    this.scrRunningAverage += scl;
    this.scrRunningAverage /= avgSample;

    // now compute the scale factor.  The scale factor is defined as that number
    // which when applied to the output samples results in full scale being
    // 12% of the DC level.

    // take 12 per cent of the DC level
    let scale = 0.12 * (this.scrRunningAverage);

    // make positive if DC level is negative
    scale = Math.abs(scale);

    // make this full scale
    scale = maxScr / scale;

    // subtract DC offset from our current sample
    let scr = (scl - this.scrRunningAverage);

    // and apply the scale factor
    scr = scr * scale;

    // clip the resulting number
    if (scr > maxScr)
    {
      scr = maxScr;
    }

    if (scr < -maxScr)
    {
      scr = -maxScr;
    }

    // scale it to match the legacy data:
    let scaledScr = ((32767 / 8.125) * scr);

    // and apply the conversion:
    // here is the conversion from the old program, let's
    // try this:
    scaledScr += 65535 / 2;
    if (scaledScr > 65535)
    {
      scaledScr = 65535;
    }

    return scaledScr; // we're done
  }

  FilterData(data: hrvData[]): hrvData[] {
      this.logger.debug(`FilterData Input: ${JSON.stringify(data)}`, [{'SourceContext': 'BleHrv'}]);
    // Algorithm per Lee Brody 11/27/2017
    // 1. Drop zeros from the data set:
    let newData = data.filter(v => v.DataValue > 0);
    this.logger.debug(`Drop 0s: ${JSON.stringify(data)}`, [{'SourceContext': 'BleHrv'}]);

    // 2. Find the median
    let newDataSorted = newData.sort((a,b) => a.DataValue - b.DataValue);
    let mid = (newDataSorted.length - 1) / 2;
    this.logger.debug(`Found Mid: ${mid} ${JSON.stringify(newDataSorted)}`, [{'SourceContext': 'BleHrv'}]);
    // if there is no newData this will return just 1 to prevent errors
    let minMedian = newData[Math.floor(mid)] ? newData[Math.floor(mid)].DataValue : 1;
    let median = mid % 1 == 0 ? (newData[mid - 1].DataValue + newData[mid].DataValue) / 2 : minMedian;
    this.logger.debug(`Found Median: ${median} ${JSON.stringify(newDataSorted)}`, [{'SourceContext': 'BleHrv'}]);

    // Iterate the array looking for skipped or extra beats
    let totalMissed = 0;
    let totalExtra = 0;
    let totalSkipped = 0;
    let filteredData: number[] = [];
    let index = 0;
    let skip = false;
    let HrvDoubleFilter = 1.75;
    let HrvExtraFilter = 0.4;
    let HrvExtra2Filter = 0.85;
    let HrvFinalFilter = 0.6;

    for(let i = 0; i < data.length; i++) {
      let dp = data[i].DataValue;
      // skip 0 values
      if (dp > 0 || skip)
      {
        // reset skip
        skip = false;

        // compare current value to median
        // if value is > 175% of median, skip
        if (dp >= HrvDoubleFilter * median)
        {
          // this is a double beat, skip it
          totalMissed++;
        }
        else
        {
          if (dp <= HrvExtraFilter * median)
          {
            // this is a potential extra beat. we need to check the next beat and
            // combine if necessary
            if (index < data.length - 1)
            {
              var nextValue = data[index + 1].DataValue;
              if (nextValue <= HrvExtra2Filter * median)
              {
                // combine this beat with the next one
                filteredData.push(dp + nextValue);
                skip = true;
                totalExtra++;
              }
              else
              {
                // skipped beat too low
                totalSkipped++;
              }
            }
          }
          else
          {
            filteredData.push(dp);
          }
        }
      }
    }

    // final pass (remove low values)
    let filteredIbi: hrvData[] = [];
    for(let i = 0; i < filteredData.length; i++) {
      let dp = filteredData[i];
      let final = HrvFinalFilter * median;
      if (dp > final)
      {
        filteredIbi.push({DataValue: dp, TimeStamp: data[i].TimeStamp});
      }
      else
      {
        // skipped beat too low
        totalSkipped++;
      }
    }

    // Calculate rejection rate based on ORIGINAL data length
    const originalLength = data.filter(v => v.DataValue > 0).length; // Exclude zeros
    const rejectionRate = totalSkipped / originalLength;

    const validThreshold = 0.15;

    if(rejectionRate > validThreshold) {
      this.logger.warn(`Filtered Data Invalid - Rejection rate: ${(rejectionRate * 100).toFixed(1)}% ` +
        `(${totalSkipped}/${originalLength} points skipped)`,
        [{'SourceContext': 'BleHrv'}]);
      this.invalidScan = true;
    }

    this.logger.info("HrvStatistics Start ------", [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`Original data points: ${data.length}`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`After removing zeros: ${originalLength}`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`Final filtered points: ${filteredIbi.length}`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`Rejection rate: ${(rejectionRate * 100).toFixed(1)}%`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`High Threshhold:${HrvDoubleFilter}`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`Low Threshhold:${HrvFinalFilter}`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`Start Extra:${HrvExtraFilter}`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`Second Extra:${HrvExtra2Filter}`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`Missed: ${totalMissed}`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`Skipped: ${totalSkipped}`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info(`Extra: ${totalExtra}`, [{'SourceContext': 'BleHrv'}]);
    this.logger.info('HrvStatistics End ------', [{'SourceContext': 'BleHrv'}]);
    return filteredIbi;
  }

  static Aa(lf: number, hf: number): number {
    let sum = (22.0 * Math.log10(hf + lf) + 5.0);
    if(sum < 20) return 20;
    return sum;
  }
  static RawBalance(lf: number, hf: number): number {
    let a = Math.sqrt(lf);
    let b = Math.sqrt(hf);
    return ((a - b) / (a + b)) + 0.16667;
  }
  static Ab(rawBalance: number) {
    let sngBalance: number;

    if(Math.abs(rawBalance) > 0.65) {
      sngBalance = 20;
    } else {
      sngBalance = (100 - (123.077 * Math.abs(rawBalance)));
    }

    if(rawBalance == 0) return 100;

    return sngBalance;
  }
  static AbIndicator(rawBalance: number): boolean {
    return rawBalance < 0;
  }

  static CalcSimpleStats(n: number, population: hrvData[], logger: LogService): hrvSimpleStats
  {
    let simpleStats: hrvSimpleStats = {
      mean: 0,
      rmsSuccessiveDifference: 0,
      variance: 0,
      standardDeviation: 0,
      averageHeartRate: 0
    };

    // sanity
    if (n <= 0)
    {
      // failed there is no data return 0 for all
      simpleStats.mean = 0;
      simpleStats.variance = 0;
      simpleStats.standardDeviation = 0;
      simpleStats.averageHeartRate = 0;
      simpleStats.rmsSuccessiveDifference = 0;
      return simpleStats;
    }

    simpleStats.mean = population.reduce((a,b) => a + b.DataValue, 0) / population.length;
    let totalVar = 0;
    for (let i = 0; i < population.length; i++)
    {
      totalVar += Math.pow(population[i].DataValue - simpleStats.mean, 2);
    }

    simpleStats.variance = totalVar / n;
    simpleStats.standardDeviation = Math.sqrt(simpleStats.variance);
    simpleStats.averageHeartRate = 60 / (simpleStats.mean / 1000);
    simpleStats.rmsSuccessiveDifference = BleHrv.RmsSuccessiveDifference(n, population, logger);
    return simpleStats;
  }

  static TransformFft(n: number, population: hrvData[], mean: number, fftBound): hrvFftStats
  {
    let fftStats: hrvFftStats = {
      aA: 0, aB: 0, aBIndicator: false, rawBalance: 0,
      fftFinal: [],
      hf: 0,
      lf: 0,
      vlf: 0,
      range: 0,
      totalPower: 0
    }
    // transform the population in the array using an FFT transform.

    // sanity
    // we should never have more than FFT_BOUND points, but just
    // in case:
    if (n > fftBound)
    {
      n = fftBound;
    }

    let data: Complex[] = [];
    for(let i = 0; i < fftBound; i++) {
      let real = i < n ? population[i].DataValue - mean : 0;
      let imag = 0;
      data.push(new Complex({
        real: real,
        imag: imag
      }))
    }

    const fft = new FFT();
    const size = data.length;
    // new fft
    fft.Radix2ForwardParallel(data);

    // Calculate PSD which is real squared plus imaginary squared AND
    // Scale the output by dividing the result by (1024 * n).
    var psdData: number[] = [];
    let sd = n * fftBound;
    for (var i = 0; i < fftBound; i++)
    {
      let temp = Math.pow(data[i].real, 2) + Math.pow(data[i].imag, 2);
      psdData.push(temp / sd);
    }

    // #12 - psdData is our array
    // #13 - accordingly, we will only consider the first 512 or FFT_BOUND/2 points in the array
    fftStats.fftFinal = psdData.slice(0, fftBound/2);

    // #14 - we will now calculate the range
    // #15 - scale according to mean ibi
    // #16 - calculate range
    fftStats.range = 500 / mean;

    fftStats.lf = BleHrv.SumFftArray(0.04, 0.15, fftStats.fftFinal, fftStats.range, fftBound);
    fftStats.vlf = BleHrv.SumFftArray(0.0033, 0.4, fftStats.fftFinal, fftStats.range, fftBound);
    fftStats.hf = BleHrv.SumFftArray(0.15, 0.4, fftStats.fftFinal, fftStats.range, fftBound);
    fftStats.totalPower = BleHrv.SumFftArray(0.0, 0.4, fftStats.fftFinal, fftStats.range, fftBound);
    fftStats.aA = BleHrv.Aa(fftStats.lf, fftStats.hf);
    fftStats.rawBalance = BleHrv.RawBalance(fftStats.lf, fftStats.hf);
    fftStats.aB = BleHrv.Ab(fftStats.rawBalance);
    fftStats.aBIndicator = BleHrv.AbIndicator(fftStats.rawBalance);

    return fftStats;
  }

  static RmsSuccessiveDifference(n: number, population: hrvData[], logger: LogService): number
  {
    // this function calculates the RMS Successive Difference
    // According to the logic provided by Lee:
    // 1. Take the difference between each element in the population
    //    and the subsequent element.
    // 2. Square that
    // 3. Sum the differences
    // 4. Take the square root of that number
    //
    // you must load the population prior to calling this function.
    // Changes 06-2024: Lee and David request to change step 3 to be the average of the differences with some filtering

    // sanity
    if (n <= 0)
    {
      return 0;
    }

    const differences: number[] = [];
    const differencesSquared: number[] = [];

    for (let i = 0; i < n - 1; i++)
    {
      // get the difference between the two elements
      let diff = population[i + 1].DataValue - population[i].DataValue;

      // add to array that we will use to filter later
      differences.push(diff);

      // square the difference
      let diffSquared = Math.pow(diff, 2);

      // push the squared difference into the array
      differencesSquared.push(diffSquared);
    }

    // take the average of the differences
    const initialDiffAvg = differencesSquared.reduce((a,b) => a + b, 0) / differencesSquared.length;

    // calculate the upper and lower limits based on 50% of the average
    const upperLimit = initialDiffAvg * 1.5;

    // filter the differences based on the limits
    const filteredDifferences = differences.filter(v => v < upperLimit);

    // now rebuild the differences squared array based on the filtered differences
    const filteredDifferencesSquared = filteredDifferences.map(v => Math.pow(v, 2));

    // now calculate the average of the filtered differences
    const diffAvg = filteredDifferencesSquared.reduce((a,b) => a + b, 0) / filteredDifferencesSquared.length;

    // take the square root of the average
    const diffSqrt = Math.sqrt(diffAvg);

    logger.debug(`RMS Successive Difference Debug Start --->`, [{'SourceContext': 'BleHrv'}]);
    logger.debug(`array of Differences: ${differences}`, [{'SourceContext': 'BleHrv'}]);
    logger.debug(`array of diff^2: ${differencesSquared}`, [{'SourceContext': 'BleHrv'}]);
    logger.debug(`dif inital avg: ${initialDiffAvg}`, [{'SourceContext': 'BleHrv'}]);
    logger.debug(`upperlimit: ${upperLimit}`, [{'SourceContext': 'BleHrv'}]);
    logger.debug(`dif filtered: ${filteredDifferences}`, [{'SourceContext': 'BleHrv'}]);
    logger.debug(`dif filtered^2: ${filteredDifferencesSquared}`, [{'SourceContext': 'BleHrv'}]);
    logger.debug(`dif avg: ${diffAvg}`, [{'SourceContext': 'BleHrv'}]);
    logger.debug(`dif avg square root: ${diffSqrt}`, [{'SourceContext': 'BleHrv'}]);
    logger.debug(`RMS Successive Difference Debug End --->`, [{'SourceContext': 'BleHrv'}]);

    return diffSqrt;
  }
  static SumFftArray(min: number, max: number, fft: number[], range: number, fftBound: number): number {
    let result = 0;
    let scaledValue: number;
    let i = 0;

    let scale = range / 512;

    if(min > range || min < 0) {
      return 0;
    }

    if(Math.abs(min) > 0) {
      // Min is non-zero.  Find the first scaled value that is greater than
      // or equal to it.
      // Since we already determined that Min is within our range, we need
      // not check the array bounds.
      while((i * scale) < min) {
        i++;
      }

      // Back up one:
      i--;

      // OK - we've determined the starting point.  We need to know what portion of
      // that first point to include.
      scaledValue = i * scale;
      result = fft[i] * ((scaledValue - min) / scaledValue);
    }

    // add all the values in the range Min to Max, but be sure we don't go off the end.
    while (((i * scale) <= max) && (i < (fftBound / 2)))
    {
      result += fft[i];
      i++;
    }

    // if we are over MAX (as opposed to off end of the array), we need a partial:
    if ((i * scale) > max)
    {
      // be sure we are not ALSO off the end of the array
      if (i < (fftBound / 2))
      {
        // determine what portion of the next element we need:
        scaledValue = i * scale;
        result += ((max - scaledValue) / max) * fft[i];
      }
    }

    return result;
  }
}

export class HrvSample {
  samples: number[] = [];
  dcOffset = 0;
  sampleCounter = 0;
  waveCounter = 0;
  counts: number[] = [];
  ibi = 0;
  ibiCounter = 0;
  maxSample = 0;
  maxSampleLast = 0;
  threshold = 0;
  thresholdCounter = 0;
  thresholdDelta = 0;
  thresholdCrossing = 0;

  thresholdOk: boolean = false;
  isAbove: boolean = false;

  constructor() {

  }


  public AddSample(sample: number): hrvSampleData {
    let data: hrvSampleData = {amplitude: 0, attackTime: 0, interval: 0, timeStamp: 0}

    sample -= this.DcOffsetFilter(sample);

    if(!this.CheckThreshold(sample)) {
      this.sampleCounter = 0;
      this.waveCounter = 0;
      this.counts = [];
      this.ibiCounter = 0;
      return undefined;
    }

    // Add sample
    this.counts.push(sample);
    this.waveCounter++;
    this.sampleCounter++;

    // LMS - we are analyzing a wave here.  Checking whether we are above or below the center line,
    // or threshold, looking for when we cross the line with a negative slope.  Which means,
    // when we were above the threshold on the last point, and are now below the threshold, we have
    // crossed.  Distance between crossings is the IBI.
    if(!this.isAbove) {
      if(sample <= this.threshold) {
        return undefined;
      }

      this.isAbove = true;
      this.ibi = this.sampleCounter;
      this.sampleCounter = 0;

      this.thresholdCrossing = this.waveCounter - 1;
      if(this.ibiCounter < 2) {
        this.ibiCounter++;
      }
    } else {

      if(sample >= (this.threshold - this.thresholdDelta)) {
        return undefined;
      }

      this.isAbove = false;
      if(this.ibiCounter == 2) {
        data.interval = this.ibi;
        // we need to determine attack time and amplitude.
        // right now in sample_buffer[0] is the last instance of the waveform crossing
        // the threshold in the negative direction, pSample points to the most recent
        // instance of the waveform crossing in the negative direction, and pThresholdCrossing (somewhere
        // in between)
        // points to the last instance of the waveform crossing the threshold in the positive direction.
        // we can determine the amplitude by finding the max and min of the samples between the last 2
        // threshold crossings....
        // get the max value in the buffer
        let max = this.counts.reduce((max, v) => max >= v ? max : v); // Math.max(...this.counts);
        let min = this.counts.reduce((min, v) => min <= v ? min : v); // Math.min(...this.counts);
        data.amplitude = max - min;

        // for attack time, start at the threshold crossing and continue until the slope of the
        // waveform decreases by a factor of 4.
        let i = this.thresholdCrossing;

        let slope = (this.counts[i + 1] - this.counts[i]) / 4;


        if (this.thresholdCrossing < this.counts.length)
        {
          for (i = this.thresholdCrossing; i < this.counts.length; i++)
          {
            if ((this.counts[i + 1] - this.counts[i]) > slope)
            {
              break;
            }
          }
        }

        // compute attack time for this portion of waveform
        let attack = i - this.thresholdCrossing;

        for (i = this.thresholdCrossing; i > 0; i--)
        {
          if ((this.counts[i] - this.counts[i - 1]) > slope)
          {
            break;
          }
        }

        // and add this time
        attack += this.thresholdCrossing - i;

        data.attackTime = attack;
        data.timeStamp = Date.now();
      }

      this.waveCounter = 0;
      return data;
    }

    return undefined;
  }

  DcOffsetFilter(sample: number): number {
    this.dcOffset *= 2999;
    this.dcOffset += sample;
    this.dcOffset /= 3000;

    return this.dcOffset;
  }

  CheckThreshold(sample: number): boolean {
    const MAX = 32767;
    const SIZE = 2000;

    if(sample > this.maxSample) {
      this.maxSample = sample;
    }

    if((this.thresholdCounter--) <= 0) {
      // threshold interval expired. If threshold has not changed
      // by 50% in either direction, mark it ok
      this.thresholdOk = ((this.maxSample >> 1) < this.maxSampleLast) && (this.maxSample > (this.maxSampleLast >> 1));

      // store the threshold as half the maximum
      this.threshold = this.maxSample / 2;

      // store threshold delta, used for hysterisis,
      this.thresholdDelta = this.threshold >> 3;

      this.maxSampleLast = this.maxSample;
      this.maxSample = -MAX;
      this.thresholdCounter = SIZE;
    }

    if(!this.thresholdOk) {
      return this.thresholdOk;
    }

    // reset an ok threshold if it is too small.
    if (this.threshold >= 60)
    {
      return this.thresholdOk;
    }

    this.maxSample = -MAX;
    this.thresholdCounter = SIZE;
    this.thresholdOk = false;
    return this.thresholdOk;
  }
}


export enum HrvStreams
{
  Pgp = 1,
  Algo = 2
}



// WEBPACK FOOTER //
// ./src/instruments/BleHrv.ts